import { ChangeDetectionStrategy, Component, computed, effect, input, output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from '@aiwyn/shared';
import { CrlRequestItem } from '../../../../models';

/**
 * The text-answer content of the item response card (Figma "Text Response"
 * states). Two states, chosen by the container's selector rules:
 *
 * - DEFAULT (`canEdit`): a bare textarea preloaded with the saved answer that
 *   silently autosaves on blur (trimmed and deduped; a cleared answer saves like
 *   any other edit) — no label, no save hint per the design.
 * - ANSWERED (not editable, or a masked answer): the answer as plain text with
 *   no input chrome. Masked answers are always read-only, mirroring legacy.
 *
 * The status/action footer is NOT here — `crl-panel-item-actions` renders it as
 * the shared card footer for questionnaire and document items alike.
 */
@Component({
  selector: 'cxp-crl-panel-questionnaire',
  templateUrl: './crl-panel-questionnaire.component.html',
  styleUrls: ['./crl-panel-questionnaire.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, ReactiveFormsModule],
})
export class CrlPanelQuestionnaireComponent {
  readonly item = input.required<CrlRequestItem>();
  /** Selector-decided editability (open items — cxp-state: rules in selectors). */
  readonly canEdit = input<boolean>(false);
  /**
   * True while a status-moving action is in flight. The input goes inert but
   * keeps its shape — swapping to the read-only view mid-submit would change the
   * card under the client while they wait on it.
   */
  readonly isSubmitting = input<boolean>(false);
  readonly answerSaved = output<string>();
  /**
   * The text as it currently reads, emitted on every keystroke. The saved answer
   * lags a blur behind, so "I'm Finished" tracks this instead — otherwise the
   * client types, finds the button still inert, and clicks nothing.
   */
  readonly answerTyped = output<string>();

  protected readonly answerControl = new FormControl<string>('', { nonNullable: true });

  protected readonly showTextarea = computed(() => this.canEdit() && !this.item().isAnswerMasked);

  /** Last value we emitted/seeded, to suppress duplicate saves on repeated blurs. */
  private lastSavedValue = '';
  private seededItemId: number | null = null;
  private isFocused = false;

  constructor() {
    // Seed the control with the saved answer, and reseed when navigating to a
    // different item. A detail reload of the SAME item (autosave/undo/finish
    // reconciles) must never clobber in-progress typing, so same-item
    // emissions only reseed while the control is untouched and unfocused.
    effect(() => {
      const item = this.item();
      const isNewItem = item.id !== this.seededItemId;
      if (!isNewItem && (this.answerControl.dirty || this.isFocused)) return;

      const saved = item.answer ?? '';
      this.seededItemId = item.id;
      this.lastSavedValue = saved;
      this.answerControl.reset(saved);
    });

    // Reactive forms own the control's enabled state, so this cannot be a
    // `[disabled]` binding on the element.
    effect(() => {
      if (this.isSubmitting()) {
        this.answerControl.disable({ emitEvent: false });
      } else {
        this.answerControl.enable({ emitEvent: false });
      }
    });
  }

  onInput(): void {
    this.answerTyped.emit(this.answerControl.value);
  }

  onFocus(): void {
    this.isFocused = true;
  }

  onBlur(): void {
    this.isFocused = false;
    const value = this.answerControl.value.trim();

    // Clearing an answer is a real edit, so an empty value saves like any other.
    // What must not save is an empty the client never typed — a control seeded
    // and blurred without being touched, or re-seeded by a reload. `dirty` tells
    // those apart, and is the same flag the seeding effect above reads to decide
    // whether the text on screen is the client's or ours.
    if (!this.answerControl.dirty || value === this.lastSavedValue) return;

    this.lastSavedValue = value;
    this.answerSaved.emit(value);
  }
}
